import React, { FC } from 'react'
import VerifyLoadIllustration from 'assets/illustrations/VerifyLoadIllustration.gif'
import SafetyIcon from 'transfix-icons/build/SafetyIcon'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { LogoTransfix } from 'transfix-ui/components/LogoTransfix'
import { Typography, makeStyles, Divider } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { IFullStop } from 'types/shipment'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { MarketplaceStopSummary } from 'apps/fleet-planner/pages/marketplace/stops/MarketplaceStopSummary'

const useStyles = makeStyles(theme => ({
  divider: {
    width: '92%',
    borderBottom: `1px solid ${brandColors.coolGray3}`,
    margin: theme.spacing(2, 0, 0),
  },
  document: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '100vh',
    marginTop: '3px',
    padding: theme.spacing(4, 2, 8),
    gap: theme.spacing(4),
  },
  loadIdText: {
    paddingLeft: theme.spacing(2),
  },
  stopsSummaryContainerMobile: {
    width: '100%',
    alignItems: 'left',
  },
  verificationImage: {
    pointerEvents: 'none',
  },
}))

interface IVerificationDisplayProps {
  stops?: IFullStop[]
  /**
   * @example 'TFX-1237715
   */
  loadIdText?: string
  verificationError?: boolean
}

const VerificationSuccessMessage = () => (
  <>
    Avoid fraud and haul with confidence. Verify the load details match your load and confirm the
    website URL. Authentic Transfix URLs will always start with <strong>apps.transfix.io</strong>.
  </>
)
const VerificationFailedMessage = 'Network error. Verification unavailable. Please try again later.'

export const VerifyShipmentDisplay: FC<IVerificationDisplayProps> = ({
  stops = [],
  loadIdText = '',
  verificationError,
}) => {
  const classes = useStyles()
  const { isMobile } = useBreakpointContext()
  const stopsSummaryContainerClassName = isMobile ? classes.stopsSummaryContainerMobile : ''
  const isVerificationSuccessful = stops.length > 0
  const verificationText = isVerificationSuccessful
    ? 'Load Details Verified'
    : 'Verify Load Details'

  return (
    <div className={classes.document}>
      {isVerificationSuccessful && (
        <InlineMessage
          message={<VerificationSuccessMessage />}
          type={INLINE_MESSAGE_VARIANTS.INFO}
          IconComponent={() => <SafetyIcon size='xlarge' />}
        />
      )}
      <LogoTransfix color='black' />
      {isVerificationSuccessful && (
        <img
          className={classes.verificationImage}
          src={VerifyLoadIllustration}
          alt='Verify Load Illustration'
        />
      )}
      <Typography variant='h2' align='center'>
        {verificationText}
      </Typography>
      <Divider className={classes.divider} />
      {verificationError && (
        <InlineMessage message={VerificationFailedMessage} type={INLINE_MESSAGE_VARIANTS.ALERT} />
      )}
      {isVerificationSuccessful && (
        <div className={stopsSummaryContainerClassName}>
          <Typography className={classes.loadIdText} variant='overline'>
            {loadIdText}
          </Typography>
          <MarketplaceStopSummary alwaysDisplayYear stops={stops} />
        </div>
      )}
    </div>
  )
}
